import * as React from 'react'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import { LineItemGroupProps } from '../LineItemGroup'
import { Dialog, DialogBody, DialogContent, DialogSurface, DialogTitle } from '@fluentui/react-components'
import { FieldArrayRenderProps } from 'formik/dist/FieldArray'
import { InvoiceProps } from '../../InvoiceForm'
import { Actions } from './Actions'
import { Body } from './Body'
import { LineItemGroupMutationDto } from '../../../../../../config/finance/invoiceConfig'
import { useTranslation } from 'react-i18next'

type Props = Pick<LineItemGroupProps, 'lineItemGroup' | 'index'> &
    Pick<InvoiceProps, 'defaultVatType' | 'lineItemEntities' | 'lineItemGroupPrices' | 'meta' | 'templates'> & {
        isNew: boolean
        open: boolean
        setOpen: (open: boolean) => void
    }

// Courtesy of https://stackoverflow.com/a/49286056
type SetProperty<K extends keyof LineItemGroupMutationDto> = (key: K, value: LineItemGroupMutationDto[K]) => void

export type LineItemGroupDialogProps = Props & {
    values: LineItemGroupMutationDto
    setValues: (values: LineItemGroupMutationDto) => void
    setProperty: SetProperty<keyof LineItemGroupMutationDto>
}

export default function LineItemGroupDialog(props: Props & FieldArrayRenderProps): ReactElement {
    const { t: translate } = useTranslation()

    const [values, setValues] = useState<LineItemGroupMutationDto>()
    const setProperty: SetProperty<keyof LineItemGroupMutationDto> = useCallback(
        (key, value) => {
            setValues({ ...values, [key]: value })
        },
        [values],
    )

    useEffect(() => {
        setValues(props.lineItemGroup)
    }, [props.open])

    if (!values) {
        return <></>
    }

    return (
        <Dialog
            open={props.open}
            onOpenChange={(_, data) => {
                if (props.isNew) {
                    props.remove(props.index)
                }
                props.setOpen(data.open)
            }}
        >
            <DialogSurface>
                <DialogBody>
                    <DialogTitle>
                        {translate(
                            props.isNew ? 'invoice.edit.form.invoicegroup.add' : 'invoice.edit.form.invoicegroup.edit',
                        )}
                    </DialogTitle>
                    <DialogContent>
                        <Body {...props} values={values} setValues={setValues} setProperty={setProperty} />
                    </DialogContent>
                    <Actions {...props} values={values} setValues={setValues} setProperty={setProperty} />
                </DialogBody>
            </DialogSurface>
        </Dialog>
    )
}
